import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowRight, MapPin, DollarSign, Heart, Zap, ChevronDown, Mail } from 'lucide-react';
import SEO from '../components/SEO';

const PERK_KEYS = [
  { key: 'pay', icon: DollarSign },
  { key: 'health', icon: Heart },
  { key: 'flex', icon: Zap },
  { key: 'remote', icon: MapPin },
];

const CareersPage = () => {
  const { t } = useTranslation('landing');
  const [openJob, setOpenJob] = useState(null);

  const perks = PERK_KEYS;

  // Six roles used to be listed here (Full-Stack Engineer, Product Designer,
  // Product Manager, Customer Success Manager, Account Executive, Technical
  // Writer). None of them were actually open, so candidates were applying to
  // positions that did not exist. The list is empty until there is real
  // hiring; the page renders an honest empty state below.
  const openings = [];

  // Applications go through the contact form, which is the one channel we
  // can verify actually delivers (POST /api/contact -> SendGrid).
  const applyHref = () => '/contact';

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO title={t('careers.seo.title')} description={t('careers.seo.description')} canonical="/careers" />
      <Navbar />

      <main className="pt-20 pb-24">
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('careers.hero.headline_a')}<br />{t('careers.hero.headline_b')}
          </h1>
          <p className="text-xl text-gray-400">
            {t('careers.hero.subtitle')}
          </p>
        </section>

        {/* Perks. Only shown while roles are actually open: the block promises
            "top-of-market salaries plus equity" and "full medical, dental,
            vision coverage", which are employment terms nobody can currently
            accept. Advertising them against an empty vacancy list is a claim
            we cannot stand behind. */}
        {openings.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {perks.map((perk) => {
              const Icon = perk.icon;
              return (
                <div key={perk.key} className="p-6 bg-white/5 rounded-2xl border border-gray-800">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">{t(`careers.perks.${perk.key}.title`)}</h3>
                  <p className="text-gray-400 text-sm">{t(`careers.perks.${perk.key}.desc`)}</p>
                </div>
              );
            })}
          </div>
        </section>
        )}

        {/* Openings */}
        <section className="max-w-4xl mx-auto px-4 py-16" id="openings">
          <h2 className="text-3xl font-bold text-white mb-8">{t('careers.openings_headline')}</h2>
          {openings.length === 0 && (
            <div className="bg-white/5 rounded-xl border border-gray-800 p-6 sm:p-8">
              <p className="text-gray-300 mb-2">{t('careers.no_openings.headline')}</p>
              <p className="text-gray-400 text-sm mb-5">{t('careers.no_openings.body')}</p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border border-gray-700 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-white/5 transition-colors"
              >
                {t('careers.cta.ask')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
          <div className="space-y-4">
            {openings.map((job, i) => {
              const expanded = openJob === i;
              return (
                <div key={i} className="bg-white/5 rounded-xl border border-gray-800 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenJob(expanded ? null : i)}
                    aria-expanded={expanded}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-white/[0.03] transition-colors group"
                  >
                    <div>
                      <h3 className="text-white font-semibold group-hover:text-amber-400 transition-colors">{job.title}</h3>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-400 mt-1">
                        <span>{job.team}</span>
                        <span className="hidden sm:inline">•</span>
                        <span>{job.location}</span>
                        <span className="hidden sm:inline">•</span>
                        <span>{job.type}</span>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                  </button>
                  {expanded && (
                    <div className="px-6 pb-6 border-t border-gray-800 pt-5">
                      <p className="text-gray-300 mb-5">{job.summary}</p>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-white text-sm font-semibold uppercase tracking-wider mb-3">{t('careers.section.responsibilities')}</h4>
                          <ul className="space-y-2 text-gray-400 text-sm">
                            {job.responsibilities.map((r, k) => <li key={k}>• {r}</li>)}
                          </ul>
                        </div>
                        <div>
                          <h4 className="text-white text-sm font-semibold uppercase tracking-wider mb-3">{t('careers.section.requirements')}</h4>
                          <ul className="space-y-2 text-gray-400 text-sm">
                            {job.requirements.map((r, k) => <li key={k}>• {r}</li>)}
                          </ul>
                        </div>
                      </div>
                      <div className="mt-6 flex flex-wrap gap-3">
                        <a
                          href={applyHref(job)}
                          className="inline-flex items-center gap-2 bg-amber-500 text-black px-5 py-2.5 rounded-lg font-medium hover:bg-amber-400 transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          {t('careers.cta.apply')}
                        </a>
                        <Link
                          to="/contact"
                          className="inline-flex items-center gap-2 border border-gray-700 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-white/5 transition-colors"
                        >
                          {t('careers.cta.ask')}
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 rounded-2xl border border-gray-800 p-12">
            <h2 className="text-3xl font-bold text-white mb-4">{t('careers.cta_box.headline')}</h2>
            <p className="text-gray-400 mb-8">{t('careers.cta_box.subtitle')}</p>
            <Link to="/contact" className="bg-amber-500 text-black px-8 py-4 rounded-xl font-semibold hover:bg-amber-400 transition-colors inline-flex items-center gap-2">
              {t('careers.cta_box.button')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CareersPage;
